import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, CheckCircle, FileText, MessageSquare, Search, Zap, Loader2, ArrowRight, UserCheck, AlertCircle, FileDown, Info, ArrowUpDown } from 'lucide-react';
import { formatDateLong, formatDateShort } from '@/utils/dateFormat';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ReliefDraftHeader, ReliefDraftTable } from '@/components/relief/ReliefDraftTable';
import { DateBucketedList } from '@/components/DateBucketedList';
import { MoreOptionsMenu } from '@/components/common/MoreOptionsMenu';

const ReliefPage = () => {
  const { token, user } = useContext(AuthContext);
  const location = useLocation();
  const deepLinkProcessedRef = useRef(false);
  const [assignments, setAssignments] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    absence_id: '',
    relief_teacher_id: '',
    date: new Date().toISOString().split('T')[0],
    period: '',
    class_name: '',
    subject: ''
  });
  // Smart Auto-Assign state
  const [smartDialogOpen, setSmartDialogOpen] = useState(false);
  const [smartAbsenceId, setSmartAbsenceId] = useState('');
  const [smartDraft, setSmartDraft] = useState(null);
  const [smartLoading, setSmartLoading] = useState(false);
  const [confirmingAssign, setConfirmingAssign] = useState(false);

  // Map absence_id -> {lesson_files, remarks, teacher_name}
  const [absenceLessonData, setAbsenceLessonData] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  // Manual assign: timetable periods for selected absence
  const [manualAbsenceId, setManualAbsenceId] = useState('');
  const [manualDraft, setManualDraft] = useState(null);
  const [manualLoading, setManualLoading] = useState(false);
  const [busyTeachersMap, setBusyTeachersMap] = useState({}); // {period: [teacher_id, ...]}
  const [busyConfirmOpen, setBusyConfirmOpen] = useState(false);
  const [pendingBusyAssign, setPendingBusyAssign] = useState(null); // {periodIndex, teacherId, teacherName, period}
  // Newly-assigned (last 24h) sort order: 'newest' | 'oldest'
  const [recentSortOrder, setRecentSortOrder] = useState('newest');

  // ─── Relief Opt-Out (P0) ─────────────────────────────────
  const [optOutDialogOpen, setOptOutDialogOpen] = useState(false);
  const [optOutForm, setOptOutForm] = useState({
    mode: 'single',         // 'single' | 'range' | 'multi'
    date: '',
    start_date: '',
    end_date: '',
    dates: '',
    start_time: '',
    end_time: '',
    reason: '',
  });
  const [myReliefOptOuts, setMyReliefOptOuts] = useState([]);
  const [allReliefOptOuts, setAllReliefOptOuts] = useState([]);
  const [optOutReviewDialogOpen, setOptOutReviewDialogOpen] = useState(false);

  const fetchMyReliefOptOuts = async () => {
    try {
      const r = await axios.get(`${API}/relief/opt-out`, { headers: { Authorization: `Bearer ${token}` } });
      setMyReliefOptOuts(r.data);
    } catch { /* silent */ }
  };
  const fetchAllReliefOptOuts = async () => {
    try {
      const r = await axios.get(`${API}/relief/opt-out`, { headers: { Authorization: `Bearer ${token}` } });
      setAllReliefOptOuts(r.data);
    } catch { /* silent */ }
  };

  const handleSubmitReliefOptOut = async (e) => {
    e.preventDefault();
    if (!optOutForm.reason.trim()) { toast.error('Reason is required'); return; }
    const payload = { reason: optOutForm.reason, mode: optOutForm.mode };
    if (optOutForm.mode === 'single') {
      if (!optOutForm.date) { toast.error('Pick a date'); return; }
      payload.dates = [optOutForm.date];
      if (optOutForm.start_time) payload.start_time = optOutForm.start_time;
      if (optOutForm.end_time) payload.end_time = optOutForm.end_time;
    } else if (optOutForm.mode === 'range') {
      if (!optOutForm.start_date || !optOutForm.end_date) { toast.error('Pick start and end dates'); return; }
      if (optOutForm.end_date < optOutForm.start_date) { toast.error('End date must be on or after start date'); return; }
      payload.start_date = optOutForm.start_date;
      payload.end_date = optOutForm.end_date;
    } else {
      const dates = optOutForm.dates.split(',').map(d => d.trim()).filter(Boolean);
      if (dates.length === 0) { toast.error('Add at least one date (comma-separated YYYY-MM-DD)'); return; }
      payload.dates = dates;
    }
    try {
      await axios.post(`${API}/relief/opt-out`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Opt-out request submitted');
      setOptOutDialogOpen(false);
      setOptOutForm({ mode: 'single', date: '', start_date: '', end_date: '', dates: '', start_time: '', end_time: '', reason: '' });
      fetchMyReliefOptOuts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit');
    }
  };

  const handleReviewReliefOptOut = async (id, status) => {
    try {
      const r = await axios.patch(`${API}/relief/opt-out/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      const detail = status === 'approved'
        ? `${r.data.reassigned_count} duty${r.data.reassigned_count === 1 ? '' : 's'} auto-reassigned${r.data.unassigned_count ? `, ${r.data.unassigned_count} unassigned` : ''}`
        : '';
      toast.success(`Opt-out ${status}${detail ? ` — ${detail}` : ''}`);
      fetchAllReliefOptOuts();
      fetchData(); // refresh duty list (reassigned rows show up)
    } catch {
      toast.error('Failed to update');
    }
  };
  // ──────────────────────────────────────────────────────────
  const handleManualAbsenceSelect = async (absenceId) => {
    setManualAbsenceId(absenceId);
    setManualDraft(null);
    setBusyTeachersMap({});
    if (!absenceId) return;
    setManualLoading(true);
    try {
      const res = await axios.post(`${API}/relief/smart-auto-assign`, { absence_id: absenceId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const draft = {
        ...res.data,
        draft_periods: res.data.draft_periods.map(p => {
          // In Manual mode we pre-fill only teacher-submitted nominations (kept so admin sees & can override)
          // Same-subject & any-available suggestions are cleared so admin makes explicit choices.
          if (p.match_type === 'nominated' && p.relief_teacher_id) {
            return { ...p };
          }
          return { ...p, relief_teacher_id: null, relief_teacher_name: null, match_type: '' };
        })
      };
      setManualDraft(draft);

      // Fetch busy teachers for this date
      const busyRes = await axios.get(`${API}/relief/busy-teachers?date=${res.data.date}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBusyTeachersMap(busyRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load timetable details');
    } finally { setManualLoading(false); }
  };

  const isTeacherBusy = (period, teacherId) => {
    return (busyTeachersMap[period] || []).includes(teacherId);
  };

  const updateManualDraftTeacher = (periodIndex, teacherId) => {
    if (!manualDraft) return;
    const period = manualDraft.draft_periods[periodIndex]?.period;
    const t = teachers.find(t => t.id === teacherId);

    if (isTeacherBusy(period, teacherId)) {
      // Use setTimeout to let the Select dropdown fully close before opening AlertDialog
      setTimeout(() => {
        setPendingBusyAssign({ periodIndex, teacherId, teacherName: t?.name || 'This teacher', period });
        setBusyConfirmOpen(true);
      }, 150);
      return;
    }

    applyManualTeacher(periodIndex, teacherId);
  };

  const applyManualTeacher = (periodIndex, teacherId) => {
    const t = teachers.find(t => t.id === teacherId);
    setManualDraft(prev => ({
      ...prev,
      draft_periods: prev.draft_periods.map((p, i) =>
        i === periodIndex ? { ...p, relief_teacher_id: teacherId, relief_teacher_name: t?.name || '' } : p
      )
    }));
  };

  const confirmBusyAssign = () => {
    if (pendingBusyAssign) {
      applyManualTeacher(pendingBusyAssign.periodIndex, pendingBusyAssign.teacherId);
    }
    setBusyConfirmOpen(false);
    setPendingBusyAssign(null);
  };

  const cancelBusyAssign = () => {
    setBusyConfirmOpen(false);
    setPendingBusyAssign(null);
  };

  const handleManualConfirm = async () => {
    if (!manualDraft) return;
    setConfirmingAssign(true);
    try {
      const confirmed = manualDraft.draft_periods
        .filter(p => p.relief_teacher_id)
        .map(p => ({
          period: p.period, class_name: p.class_name, subject: p.subject,
          relief_teacher_id: p.relief_teacher_id, match_type: 'manual',
          period_files: p.period_files, period_remarks: p.period_remarks,
        }));
      const res = await axios.post(`${API}/relief/confirm-assignments`, {
        absence_id: manualDraft.absence_id, assignments: confirmed
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setDialogOpen(false);
      setManualDraft(null);
      setManualAbsenceId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm assignments');
    } finally { setConfirmingAssign(false); }
  };

  const filteredAssignments = useMemo(() => {
    const sorted = [...assignments].sort((a, b) => (a.date || '').localeCompare(b.date || '') || (a.relief_teacher_name || '').localeCompare(b.relief_teacher_name || ''));
    if (!searchQuery.trim()) return sorted;
    const q = searchQuery.toLowerCase();
    return sorted.filter(a =>
      (a.relief_teacher_name || '').toLowerCase().includes(q) ||
      (a.absent_teacher_name || '').toLowerCase().includes(q) ||
      (a.class_name || '').toLowerCase().includes(q) ||
      (a.date || '').includes(q) ||
      (a.subject || '').toLowerCase().includes(q)
    );
  }, [assignments, searchQuery]);

  const { newlyAssigned, restAssignments } = useMemo(() => {
    const now = new Date();
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
    const recent = [];
    const rest = [];
    filteredAssignments.forEach(a => {
      if (a.created_at && a.created_at >= oneDayAgo) {
        recent.push(a);
      } else {
        rest.push(a);
      }
    });
    // Order recent by created_at according to user preference
    recent.sort((a, b) => {
      const ka = a.created_at || '';
      const kb = b.created_at || '';
      return recentSortOrder === 'newest' ? kb.localeCompare(ka) : ka.localeCompare(kb);
    });
    return { newlyAssigned: recent, restAssignments: rest };
  }, [filteredAssignments, recentSortOrder]);

  useEffect(() => {
    fetchData();
  }, []);

  // Handle deep-link from Admin Absences page: ?absence_id=...&mode=smart
  useEffect(() => {
    if (deepLinkProcessedRef.current) return;
    if (!absences || absences.length === 0) return;
    const params = new URLSearchParams(location.search);
    const absId = params.get('absence_id');
    const mode = params.get('mode');
    if (!absId || mode !== 'smart') return;
    const exists = absences.find(a => a.id === absId);
    if (!exists) return;
    deepLinkProcessedRef.current = true;
    setSmartDialogOpen(true);
    setSmartAbsenceId(absId);
    // Auto-run smart draft once state settles
    setTimeout(() => { handleSmartAutoAssign(absId); }, 150);
  }, [absences, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const isAdmin = user?.role === 'admin';
      const [assignmentsRes, absencesRes, teachersRes] = await Promise.all([
        axios.get(`${API}/relief`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/absences`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        isAdmin
          ? axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
          : axios.get(`${API}/users/teachers-list`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      setAssignments(assignmentsRes.data);
      setAbsences((absencesRes.data || []).filter(a => a.status === 'pending'));
      setTeachers((teachersRes.data || []).filter(u => (u.role === 'teacher' || !u.role) && (u.is_active !== false)));

      // Fetch lesson files for each unique absence linked to assignments
      const uniqueAbsenceIds = [...new Set(assignmentsRes.data.map(a => a.absence_id).filter(Boolean))];
      const lessonMap = {};
      await Promise.all(
        uniqueAbsenceIds.map(async (absId) => {
          try {
            const res = await axios.get(`${API}/absences/${absId}/lesson-files`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            lessonMap[absId] = res.data;
          } catch {
            // silently skip if not authorized or not found
          }
        })
      );
      setAbsenceLessonData(lessonMap);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/relief`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Relief assignment created successfully');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create assignment');
    }
  };

  const handleAcknowledge = async (assignmentId) => {
    try {
      await axios.patch(`${API}/relief/${assignmentId}/acknowledge`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Assignment acknowledged');
      fetchData();
    } catch (error) {
      toast.error('Failed to acknowledge assignment');
    }
  };

  const resetForm = () => {
    setFormData({
      absence_id: '',
      relief_teacher_id: '',
      date: new Date().toISOString().split('T')[0],
      period: '',
      class_name: '',
      subject: ''
    });
  };

  const handleSmartAutoAssign = async (overrideAbsenceId) => {
    // When called from a click handler the first arg is a SyntheticEvent; only accept strings.
    const absId = (typeof overrideAbsenceId === 'string' && overrideAbsenceId) ? overrideAbsenceId : smartAbsenceId;
    if (!absId) { toast.error('Select an absence first'); return; }
    setSmartLoading(true);
    try {
      const res = await axios.post(`${API}/relief/smart-auto-assign`, { absence_id: absId }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmartDraft(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate auto-assign draft');
    } finally { setSmartLoading(false); }
  };

  const updateDraftTeacher = (periodIndex, teacherId) => {
    if (!smartDraft) return;
    const t = teachers.find(t => t.id === teacherId);
    const updated = { ...smartDraft, draft_periods: smartDraft.draft_periods.map((p, i) =>
      i === periodIndex ? { ...p, relief_teacher_id: teacherId, relief_teacher_name: t?.name || '' } : p
    )};
    setSmartDraft(updated);
  };

  const handleConfirmAssignments = async () => {
    if (!smartDraft) return;
    setConfirmingAssign(true);
    try {
      const confirmed = smartDraft.draft_periods
        .filter(p => p.relief_teacher_id)
        .map(p => ({
          period: p.period,
          class_name: p.class_name,
          subject: p.subject,
          relief_teacher_id: p.relief_teacher_id,
          match_type: p.match_type,
          period_files: p.period_files,
          period_remarks: p.period_remarks,
        }));
      const res = await axios.post(`${API}/relief/confirm-assignments`, {
        absence_id: smartDraft.absence_id,
        assignments: confirmed
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      setSmartDialogOpen(false);
      setSmartDraft(null);
      setSmartAbsenceId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm assignments');
    } finally { setConfirmingAssign(false); }
  };

  const isAdmin = user?.role === 'admin';

  return (
    <>
    <div data-testid="relief-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-slate-900">Relief Duties</h1>
          <p className="text-sm sm:text-base text-slate-500 mt-2">Manage relief teacher assignments</p>
        </div>
        {isAdmin ? (
          <div className="flex flex-row flex-wrap gap-2 w-full sm:w-auto items-center">
          <TooltipProvider delayDuration={200}>
            {/* Mobile: secondary actions collapsed via the shared
                MoreOptionsMenu. Desktop keeps the buttons inline below. */}
            <MoreOptionsMenu
              className="md:hidden"
              testid="relief-more-actions-button"
              groups={[
                {
                  label: 'Manage',
                  items: [
                    { label: 'Opt-Out Requests', icon: <UserCheck className="h-4 w-4" />, onClick: () => { setOptOutReviewDialogOpen(true); fetchAllReliefOptOuts(); }, testid: 'mobile-relief-optout-review-button' },
                    { label: 'Manual Assign', icon: <Plus className="h-4 w-4" />, onClick: () => setDialogOpen(true), testid: 'mobile-relief-manual-assign-button' },
                  ],
                },
              ]}
            />
            {/* Opt-Out Requests Review (Admin) — desktop only, mobile uses dropdown above */}
            <Button
              variant="outline"
              className="hidden md:inline-flex border-slate-300"
              onClick={() => { setOptOutReviewDialogOpen(true); fetchAllReliefOptOuts(); }}
              data-testid="relief-optout-review-button"
            >
              <UserCheck className="mr-2 h-4 w-4" />
              Opt-Out Requests
            </Button>
            {/* Smart Auto-Assign (Primary) */}
            <Dialog open={smartDialogOpen} onOpenChange={(open) => { setSmartDialogOpen(open); if (!open) { setSmartDraft(null); setSmartAbsenceId(''); } }}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DialogTrigger asChild>
                    <Button data-testid="smart-auto-assign-button" className="flex-1 md:flex-initial bg-emerald-700 hover:bg-emerald-800 text-white">
                      <Zap className="mr-2 h-4 w-4" />
                      Smart Auto-Assign
                      <Info className="ml-1.5 h-3.5 w-3.5 opacity-60" />
                    </Button>
                  </DialogTrigger>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-left p-3">
                  <p className="text-xs font-semibold mb-1">Recommended</p>
                  <p className="text-xs text-slate-300">Automatically pulls the absent teacher's timetable, finds available relief teachers using the smart engine (subject match, workload balance), and presents a draft for your review before confirming.</p>
                </TooltipContent>
              </Tooltip>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
                <DialogHeader>
                  <DialogTitle>Smart Auto-Assign Relief Duties</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <Label>Select Absence</Label>
                      <Select value={smartAbsenceId} onValueChange={setSmartAbsenceId}>
                        <SelectTrigger className="mt-1.5" data-testid="smart-absence-select">
                          <SelectValue placeholder="Choose an absence to auto-assign" />
                        </SelectTrigger>
                        <SelectContent>
                          {absences.map(a => (
                            <SelectItem key={a.id} value={a.id}>{a.teacher_name} — {formatDateShort(a.date)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={handleSmartAutoAssign} disabled={!smartAbsenceId || smartLoading}
                      className="bg-blue-900 hover:bg-blue-800" data-testid="generate-draft-button">
                      {smartLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Zap className="mr-2 h-4 w-4" />}
                      {smartLoading ? 'Generating...' : 'Generate Draft'}
                    </Button>
                  </div>
                  {smartDraft && (
                    <div className="space-y-4" data-testid="smart-draft-view">
                      <ReliefDraftHeader draft={smartDraft} mode="smart" />
                      <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900" data-testid="smart-availability-banner">
                        <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                        <span>
                          This list shows <strong>only available teachers</strong> for each period.
                          If you need to override and assign a teacher who has another class, use <strong>Manual Assign</strong> instead.
                        </span>
                      </div>
                      <ReliefDraftTable
                        draft={smartDraft}
                        teachers={teachers}
                        mode="smart"
                        onTeacherChange={updateDraftTeacher}
                        testIdPrefix="draft"
                      />
                      <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                        <p className="text-xs text-slate-500">Review the assignments above. Change relief teachers if needed, then confirm.</p>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => { setSmartDraft(null); setSmartAbsenceId(''); }}>Reset</Button>
                          <Button onClick={handleConfirmAssignments} disabled={confirmingAssign || !smartDraft.draft_periods.some(p => p.relief_teacher_id)}
                            className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-assignments-button">
                            {confirmingAssign ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserCheck className="mr-2 h-4 w-4" />}
                            {confirmingAssign ? 'Confirming...' : `Confirm & Notify (${smartDraft.draft_periods.filter(p => p.relief_teacher_id).length})`}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Manual Assign (Secondary) */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="hidden md:inline-flex" data-testid="create-assignment-button">
                      <Plus className="mr-2 h-4 w-4" />
                      Manual Assign
                      <Info className="ml-1.5 h-3.5 w-3.5 opacity-60" />
                    </Button>
                  </DialogTrigger>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs text-left p-3">
                  <p className="text-xs font-semibold mb-1">For special cases</p>
                  <p className="text-xs text-slate-300">Manually assign a single relief period. Select an absence to auto-fill timetable details, then pick the relief teacher yourself. Use when you need precise control over a specific period.</p>
                </TooltipContent>
              </Tooltip>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full">
                <DialogHeader>
                  <DialogTitle>Manual Relief Assignment</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <Label>Select Absence</Label>
                      <Select value={manualAbsenceId} onValueChange={handleManualAbsenceSelect}>
                        <SelectTrigger className="mt-1.5" data-testid="manual-absence-select">
                          <SelectValue placeholder="Choose an absence" />
                        </SelectTrigger>
                        <SelectContent>
                          {absences.map(a => (
                            <SelectItem key={a.id} value={a.id}>{a.teacher_name} — {formatDateShort(a.date)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {manualLoading && <Loader2 className="h-5 w-5 text-slate-400 animate-spin" />}
                  </div>

                  {manualDraft && (
                    <div className="space-y-4" data-testid="manual-draft-view">
                      <ReliefDraftHeader draft={manualDraft} mode="manual" />
                      <ReliefDraftTable
                        draft={manualDraft}
                        teachers={teachers}
                        mode="manual"
                        onTeacherChange={updateManualDraftTeacher}
                        isTeacherBusy={isTeacherBusy}
                        testIdPrefix="manual"
                      />
                      <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                        <p className="text-xs text-slate-500">Select a relief teacher for each period, then confirm to save and notify.</p>
                        <div className="flex gap-2">
                          <Button variant="outline" onClick={() => { setManualDraft(null); setManualAbsenceId(''); }}>Reset</Button>
                          <Button onClick={handleManualConfirm} disabled={confirmingAssign || !manualDraft.draft_periods.some(p => p.relief_teacher_id)}
                            className="bg-emerald-600 hover:bg-emerald-700" data-testid="manual-confirm-button">
                            {confirmingAssign ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserCheck className="mr-2 h-4 w-4" />}
                            {confirmingAssign ? 'Confirming...' : `Confirm & Notify (${manualDraft.draft_periods.filter(p => p.relief_teacher_id).length})`}
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </DialogContent>
              <AlertDialog open={busyConfirmOpen} onOpenChange={setBusyConfirmOpen}>
                <AlertDialogContent data-testid="busy-confirm-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
                      <AlertCircle className="h-5 w-5" />
                      Assign Unavailable Teacher?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-sm text-slate-600">
                      <strong>{pendingBusyAssign?.teacherName}</strong> is currently unavailable during <strong>{pendingBusyAssign?.period}</strong> (already teaching or assigned to another relief duty).
                      <br /><br />
                      Assigning them will create a scheduling conflict. Continue only if you have confirmed with the teacher directly.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={cancelBusyAssign} data-testid="busy-cancel-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={confirmBusyAssign} className="bg-amber-600 hover:bg-amber-700" data-testid="busy-confirm-btn">
                      Yes, Assign Anyway
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
          </Dialog>
          </TooltipProvider>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              className="w-full sm:w-auto border-slate-300"
              onClick={() => { setOptOutDialogOpen(true); fetchMyReliefOptOuts(); }}
              data-testid="request-relief-optout-button"
            >
              <AlertCircle className="mr-2 h-4 w-4" />
              Request Opt-Out
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-slate-600">Loading assignments...</div>
      ) : assignments.length === 0 ? (
        <Card className="p-8 text-center" data-testid="no-assignments-message">
          <p className="text-slate-500">No relief assignments yet.</p>
        </Card>
      ) : (
        <>
        <div className="relative mb-4" data-testid="relief-search">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search by teacher, class, subject, or date..." className="pl-9" data-testid="relief-search-input" />
        </div>
        {filteredAssignments.length === 0 ? (
          <Card className="p-8 text-center"><p className="text-slate-500">No assignments matching "{searchQuery}"</p></Card>
        ) : (
        <div className="space-y-6">
          {/* Newly Assigned Section */}
          {newlyAssigned.length > 0 && (
            <div data-testid="newly-assigned-section">
              <div className="flex items-center gap-3 mb-3">
                <h3 className="text-sm font-semibold text-emerald-700 uppercase tracking-wider">Newly Assigned (Last 24 Hours)</h3>
                <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700">{newlyAssigned.length}</span>
                <button
                  type="button"
                  onClick={() => setRecentSortOrder(o => o === 'newest' ? 'oldest' : 'newest')}
                  className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-emerald-200 bg-white text-[11px] font-medium text-emerald-800 hover:bg-emerald-50 transition-colors"
                  data-testid="recent-relief-sort-toggle"
                  title="Toggle sort order"
                >
                  <ArrowUpDown className="h-3 w-3" />
                  {recentSortOrder === 'newest' ? 'Newest First' : 'Oldest First'}
                </button>
              </div>
              <div className="space-y-3">
                {newlyAssigned.map((assignment) => (
                  <Card key={assignment.id} className="dashboard-card p-4 sm:p-6 border-l-4 border-l-emerald-400" data-testid="assignment-card">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                          <h3 className="text-base sm:text-lg font-semibold text-slate-900">{assignment.relief_teacher_name}</h3>
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">New</span>
                          {assignment.is_acknowledged ? (
                            <span className="flex items-center px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded"><CheckCircle className="mr-1 h-3 w-3" />Acknowledged</span>
                          ) : (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-medium rounded">Pending</span>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-600">
                          <p><strong>Date:</strong> {formatDateLong(assignment.date)}</p>
                          <p><strong>Period:</strong> {assignment.period}</p>
                          <p><strong>Class:</strong> {assignment.class_name}</p>
                          <p><strong>Subject:</strong> {assignment.subject}</p>
                          <p className="sm:col-span-2"><strong>Covering for:</strong> {assignment.absent_teacher_name}</p>
                        </div>
                        {absenceLessonData[assignment.absence_id] && (() => { const data = absenceLessonData[assignment.absence_id]; const hasFiles = data.lesson_files && data.lesson_files.length > 0; const hasRemarks = data.remarks && data.remarks.trim(); if (!hasFiles && !hasRemarks) return null; return (<div className="mt-3 p-3 bg-blue-50/70 border border-blue-100 rounded-md" data-testid="lesson-info-section"><p className="text-xs font-semibold text-blue-900 mb-1.5 flex items-center gap-1"><MessageSquare className="h-3 w-3" />Lesson Instructions from {data.teacher_name}</p>{hasRemarks && <p className="text-sm text-slate-700 mb-2">{data.remarks}</p>}{hasFiles && <div className="flex flex-wrap gap-2">{data.lesson_files.map(file => <a key={file.id} href={`${API}/files/${file.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 bg-white text-blue-700 text-xs font-medium rounded border border-blue-200 hover:bg-blue-100 transition-colors" data-testid="relief-lesson-file-link"><FileText className="h-3 w-3" />{file.filename}</a>)}</div>}</div>); })()}
                      </div>
                      {!assignment.is_acknowledged && user?.id === assignment.relief_teacher_id && (
                        <Button onClick={() => handleAcknowledge(assignment.id)} size="sm" className="bg-blue-900 hover:bg-blue-800 w-full sm:w-auto sm:ml-4" data-testid="acknowledge-button"><CheckCircle className="mr-2 h-4 w-4" />Acknowledge</Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Date-Bucketed Sections (Upcoming → Last 3 Days → Expired, by month) */}
          <DateBucketedList
            items={restAssignments}
            getDate={(a) => a.date}
            testidPrefix="relief-bucket"
            emptyLabel="No older relief duties."
            renderItem={(assignment) => (
            <Card key={assignment.id} className="dashboard-card p-4 sm:p-6" data-testid="assignment-card">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                    <h3 className="text-base sm:text-lg font-semibold text-slate-900">{assignment.relief_teacher_name}</h3>
                    {assignment.is_acknowledged ? (
                      <span className="flex items-center px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                        <CheckCircle className="mr-1 h-3 w-3" />
                        Acknowledged
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs font-medium rounded">
                        Pending
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-slate-600">
                    <p><strong>Date:</strong> {assignment.date}</p>
                    <p><strong>Period:</strong> {assignment.period}</p>
                    <p><strong>Class:</strong> {assignment.class_name}</p>
                    <p><strong>Subject:</strong> {assignment.subject}</p>
                    <p className="sm:col-span-2"><strong>Covering for:</strong> {assignment.absent_teacher_name}
                      {assignment.match_type && assignment.match_type !== 'any' && (
                        <span className={`ml-2 px-1.5 py-0.5 text-[10px] font-medium rounded ${assignment.match_type === 'same_subject' ? 'bg-emerald-100 text-emerald-700' : assignment.match_type === 'nominated' ? 'bg-blue-100 text-blue-700' : ''}`}>
                          {assignment.match_type === 'same_subject' ? 'Same Subject' : assignment.match_type === 'nominated' ? 'Nominated' : ''}
                        </span>
                      )}
                    </p>
                  </div>
                  {/* Period-specific files */}
                  {assignment.period_files && assignment.period_files.length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50/70 border border-blue-100 rounded-md" data-testid="period-files-section">
                      <p className="text-xs font-semibold text-blue-900 mb-1.5 flex items-center gap-1">
                        <FileText className="h-3 w-3" />Lesson Materials for {assignment.period} — {assignment.class_name}
                      </p>
                      {assignment.period_remarks && <p className="text-sm text-slate-700 mb-2">{assignment.period_remarks}</p>}
                      <div className="flex flex-wrap gap-2">
                        {assignment.period_files.map(file => (
                          <a key={file.id} href={`${API}/files/${file.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 bg-white text-blue-700 text-xs font-medium rounded border border-blue-200 hover:bg-blue-100 transition-colors" data-testid="period-file-link">
                            <FileText className="h-3 w-3" />{file.filename}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Lesson instructions & files from absent teacher */}
                  {absenceLessonData[assignment.absence_id] && (
                    (() => {
                      const data = absenceLessonData[assignment.absence_id];
                      const hasFiles = data.lesson_files && data.lesson_files.length > 0;
                      const hasRemarks = data.remarks && data.remarks.trim();
                      if (!hasFiles && !hasRemarks) return null;
                      return (
                        <div className="mt-3 p-3 bg-blue-50/70 border border-blue-100 rounded-md" data-testid="lesson-info-section">
                          <p className="text-xs font-semibold text-blue-900 mb-1.5 flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />
                            Lesson Instructions from {data.teacher_name}
                          </p>
                          {hasRemarks && (
                            <p className="text-sm text-slate-700 mb-2">{data.remarks}</p>
                          )}
                          {hasFiles && (
                            <div className="flex flex-wrap gap-2">
                              {data.lesson_files.map((file) => (
                                <a
                                  key={file.id}
                                  href={`${API}/files/${file.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-2 py-1 bg-white text-blue-700 text-xs font-medium rounded border border-blue-200 hover:bg-blue-100 transition-colors"
                                  data-testid="relief-lesson-file-link"
                                >
                                  <FileText className="h-3 w-3" />
                                  {file.filename}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()
                  )}
                </div>
                {!assignment.is_acknowledged && user?.id === assignment.relief_teacher_id && (
                  <Button
                    onClick={() => handleAcknowledge(assignment.id)}
                    size="sm"
                    className="bg-blue-900 hover:bg-blue-800 w-full sm:w-auto sm:ml-4"
                    data-testid="acknowledge-button"
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Acknowledge
                  </Button>
                )}
              </div>
            </Card>
            )}
          />
        </div>
        )}
        </>
      )}
    </div>

    {/* ─── Teacher: Request Relief Opt-Out Dialog ─── */}
    <Dialog open={optOutDialogOpen} onOpenChange={setOptOutDialogOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Request Relief Opt-Out</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">Requests must be at least <strong>72 hours</strong> in advance. For shorter notice, use the "Unable to Cover" button on your relief duty email.</p>
        <form onSubmit={handleSubmitReliefOptOut} className="space-y-4 mt-2">
          <div className="grid grid-cols-3 gap-2" data-testid="relief-optout-mode-picker">
            {[
              { key: 'single', label: 'Single Day' },
              { key: 'range', label: 'Date Range' },
              { key: 'multi', label: 'Multiple Days' },
            ].map(opt => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setOptOutForm(f => ({ ...f, mode: opt.key }))}
                data-testid={`relief-optout-mode-${opt.key}`}
                className={`px-3 py-2 text-xs font-medium rounded-md border transition ${optOutForm.mode === opt.key ? 'bg-blue-900 text-white border-blue-900' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
              >{opt.label}</button>
            ))}
          </div>

          {optOutForm.mode === 'single' && (
            <div className="space-y-3">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={optOutForm.date}
                  onChange={(e) => setOptOutForm({ ...optOutForm, date: e.target.value })}
                  data-testid="relief-optout-single-date"
                />
              </div>
              <div>
                <Label className="flex items-center justify-between">
                  <span>Time-frame <span className="text-slate-400 font-normal">(optional)</span></span>
                  <span className="text-xs text-slate-400 font-normal">Leave blank for whole day</span>
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input type="time" value={optOutForm.start_time} onChange={(e) => setOptOutForm({ ...optOutForm, start_time: e.target.value })} data-testid="relief-optout-start-time" className="flex-1" />
                  <span className="text-slate-400">–</span>
                  <Input type="time" value={optOutForm.end_time} onChange={(e) => setOptOutForm({ ...optOutForm, end_time: e.target.value })} data-testid="relief-optout-end-time" className="flex-1" />
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Only relief duties whose period time overlaps this window will be blocked.</p>
              </div>
            </div>
          )}

          {optOutForm.mode === 'range' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start Date</Label>
                <Input type="date" value={optOutForm.start_date} onChange={(e) => setOptOutForm({ ...optOutForm, start_date: e.target.value })} data-testid="relief-optout-start-date" />
              </div>
              <div>
                <Label>End Date</Label>
                <Input type="date" value={optOutForm.end_date} onChange={(e) => setOptOutForm({ ...optOutForm, end_date: e.target.value })} data-testid="relief-optout-end-date" />
              </div>
            </div>
          )}

          {optOutForm.mode === 'multi' && (
            <div>
              <Label>Dates (comma-separated YYYY-MM-DD)</Label>
              <Input value={optOutForm.dates} onChange={(e) => setOptOutForm({ ...optOutForm, dates: e.target.value })} placeholder="2026-06-01, 2026-06-02" data-testid="relief-optout-dates-input" />
            </div>
          )}

          <div>
            <Label>Reason</Label>
            <textarea
              value={optOutForm.reason}
              onChange={(e) => setOptOutForm({ ...optOutForm, reason: e.target.value })}
              rows={3}
              placeholder="Medical appointment, personal matter, etc."
              className="w-full rounded-md border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="relief-optout-reason-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOptOutDialogOpen(false)}>Cancel</Button>
            <Button type="submit" className="bg-blue-900 hover:bg-blue-800" data-testid="relief-optout-submit-button">Submit Request</Button>
          </div>
        </form>
        {myReliefOptOuts.length > 0 && (
          <div className="mt-4 pt-3 border-t border-slate-100">
            <p className="text-xs font-semibold text-slate-500 mb-2">My Previous Requests</p>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {myReliefOptOuts.map(o => (
                <div key={o.id} className="flex justify-between items-center text-xs p-2 bg-slate-50 rounded">
                  <span>
                    {o.dates.join(', ')}
                    {(o.start_time || o.end_time) && (
                      <span className="ml-1 text-slate-500">({o.start_time || '00:00'}–{o.end_time || '23:59'})</span>
                    )}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full font-medium ${o.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : o.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{o.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* ─── Admin: Relief Opt-Out Review Dialog ─── */}
    <Dialog open={optOutReviewDialogOpen} onOpenChange={setOptOutReviewDialogOpen}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Relief Opt-Out Requests</DialogTitle>
        </DialogHeader>
        {allReliefOptOuts.length === 0 ? (
          <p className="text-sm text-slate-500 py-3">No opt-out requests.</p>
        ) : (
          <div className="space-y-2">
            {allReliefOptOuts.map(o => (
              <div key={o.id} className="border border-slate-200 rounded-md p-3" data-testid={`relief-optout-row-${o.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">{o.user_name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Dates: <strong>{o.dates.join(', ')}</strong>
                      {(o.start_time || o.end_time) && (
                        <span className="ml-1 text-slate-600">· Time: <strong>{o.start_time || '00:00'}–{o.end_time || '23:59'}</strong></span>
                      )}
                    </p>
                    <p className="text-xs text-slate-600 mt-1">{o.reason}</p>
                    {o.status === 'approved' && (typeof o.reassigned_count === 'number' || typeof o.unassigned_count === 'number') && (
                      <p className="text-[10px] text-emerald-700 mt-1">{o.reassigned_count || 0} duty{(o.reassigned_count || 0) === 1 ? '' : 's'} auto-reassigned{o.unassigned_count ? `, ${o.unassigned_count} unassigned` : ''}</p>
                    )}
                    {o.reviewed_by && <p className="text-[10px] text-slate-400 mt-1">Reviewed by {o.reviewed_by} at {o.reviewed_at?.slice(0, 16)}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${o.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : o.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{o.status}</span>
                    {o.status === 'pending' && (
                      <>
                        <Button size="sm" onClick={() => handleReviewReliefOptOut(o.id, 'approved')} className="bg-emerald-600 hover:bg-emerald-700 h-7 text-xs" data-testid={`relief-optout-approve-${o.id}`}>Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => handleReviewReliefOptOut(o.id, 'rejected')} className="h-7 text-xs border-red-300 text-red-700" data-testid={`relief-optout-reject-${o.id}`}>Reject</Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ReliefPage;